import React from "react";
import { cn } from "@/lib/utils";
import {
  FileText,
  CreditCard,
  RotateCcw,
  Gavel,
  Database,
  ArrowLeftRight,
  Package,
  Wrench,
  HeartHandshake,
  CalendarClock,
  BookOpen,
  Medal,
  Sparkles
} from "lucide-react";

const ICONS = {
  FileText,
  CreditCard,
  RotateCcw,
  Gavel,
  Database,
  ArrowLeftRight,
  Package,
  Wrench,
  HeartHandshake,
  CalendarClock,
  BookOpen
};

// Pointy-top hexagon — the medallion silhouette.
const HEX = "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)";

const TIER = {
  gold: {
    label: "Gold",
    rank: "Jedi Master",
    core: "bg-gradient-to-br from-amber-200 via-amber-400 to-yellow-600",
    ring: "#fcd34d",
    glow: "rgba(252,211,77,0.5)",
    spark: "text-amber-300"
  },
  silver: {
    label: "Silver",
    rank: "Jedi Knight",
    core: "bg-gradient-to-br from-slate-100 via-slate-300 to-slate-500",
    ring: "#cbd5e1",
    glow: "rgba(148,163,184,0.45)",
    spark: "text-cyan-300"
  },
  bronze: {
    label: "Bronze",
    rank: "Padawan",
    core: "bg-gradient-to-br from-orange-200 via-orange-400 to-amber-700",
    ring: "#fdba74",
    glow: "rgba(251,146,60,0.45)",
    spark: "text-orange-300"
  }
};

const SIZES = {
  sm: "w-14 h-14",
  md: "w-20 h-20",
  lg: "w-24 h-24"
};
const ICON_SIZES = {
  sm: "w-5 h-5",
  md: "w-7 h-7",
  lg: "w-9 h-9"
};
const SPARK_SIZES = {
  sm: "w-3 h-3",
  md: "w-4 h-4",
  lg: "w-5 h-5"
};

export default function Badge({ topic, tier, earned = true, size = "md" }) {
  const Icon = ICONS[topic?.icon] || Medal;
  const t = TIER[tier?.key] || TIER.bronze;

  return (
    <div className="flex flex-col items-center text-center">
      <div className={cn("relative", SIZES[size])}>
        {/* cosmic glow halo */}
        {earned && (
          <div
            className="absolute inset-0 blur-xl rounded-full animate-medallion-pulse"
            style={{ background: t.glow }}
          />
        )}
        {/* neon ring edge */}
        {earned && (
          <div
            className="absolute inset-[-2px]"
            style={{ clipPath: HEX, background: t.ring }}
          />
        )}
        {/* deep-space metallic frame */}
        <div
          className="absolute inset-0"
          style={{
            clipPath: HEX,
            background:
              "linear-gradient(160deg, #1b1030 0%, #0b0717 60%, #2a1a4a 100%)",
            filter: earned ? `drop-shadow(0 0 6px ${t.glow})` : "none"
          }}
        />
        {/* kyber crystal core */}
        <div
          className={cn(
            "absolute inset-[3px] flex items-center justify-center",
            earned ? t.core : "bg-muted"
          )}
          style={{ clipPath: HEX }}
        >
          <Icon
            className={cn(
              ICON_SIZES[size],
              earned ? "text-white" : "text-muted-foreground"
            )}
            strokeWidth={1.5}
            style={
              earned
                ? { filter: "drop-shadow(0 0 4px rgba(255,255,255,0.65))" }
                : {}
            }
          />
        </div>
        {/* stellar sparkle accents */}
        {earned && (
          <>
            <Sparkles
              className={cn(
                "absolute -top-1 -right-1",
                SPARK_SIZES[size],
                t.spark
              )}
              strokeWidth={1.5}
            />
            <Sparkles
              className={cn(
                "absolute -bottom-1 -left-1",
                SPARK_SIZES[size],
                t.spark,
                "opacity-70"
              )}
              strokeWidth={1.5}
            />
          </>
        )}
      </div>
      <span className="mt-2.5 text-xs font-semibold text-foreground leading-tight">
        {topic?.label}
      </span>
      <span
        className={cn(
          "text-[11px] font-medium tracking-wide",
          earned ? "text-muted-foreground" : "text-muted-foreground/60"
        )}
      >
        {earned ? t.rank : "Locked"}
      </span>
    </div>
  );
}